import useEventEmitter from '@core/hooks/useEventEmitter'
import useApiRequest from '@core/hooks/useApiRequest'
import { fetchFeedbackThemes, sendFeedback } from '@/modules/Feedback/services/feedback'
import { computed, reactive, ref } from 'vue'
import FeedbackTheme from '@/modules/Feedback/models/FeedbackTheme'
import type { FeedbackFormData } from '@feedback/services/types'
import { AnswerType } from '@feedback/services/types'

export const initialFormData: FeedbackFormData = {
  theme: null,
  comment: '',
  answerType: AnswerType.Email
}

const normalizeErrors = (responseErrors: unknown): string[] => {
  if (Array.isArray(responseErrors)) return responseErrors.map(String)
  if (typeof responseErrors === 'string') return [responseErrors]
  if (!responseErrors || typeof responseErrors !== 'object') return []

  return Object.values(responseErrors).reduce<string[]>((result, error) => {
    const messages = Array.isArray(error) ? error : [error]
    return [...result, ...messages.filter(Boolean).map(String)]
  }, [])
}

const FeedbackController = (initialPhone?: string | null, initialEmail?: string | null) => {
  const { $emit } = useEventEmitter()

  // form data
  const formData = reactive({ ...initialFormData, phone: initialPhone, email: initialEmail })
  formData.files = []

  // themes
  const themes = ref<FeedbackTheme[] | null>(null)
  const themesOptions = computed(() => themes.value?.map(o => ({ label: o.label, value: o.id })))
  const feedbackThemesRequest = useApiRequest(fetchFeedbackThemes)
  feedbackThemesRequest.$on('success', responseData => {
    themes.value = responseData.map(item => new FeedbackTheme(item))
    $emit('feedback-themes-success')
  })
  feedbackThemesRequest.$on('error', errors => {
    $emit('feedback-themes-error', errors)
  })
  const requestFeedbackThemes = () => {
    feedbackThemesRequest.request()
  }

  // send
  const errors = ref<string[]>([])
  const isProcessingSendFeedback = ref(false)
  const sendFeedbackRequest = useApiRequest(sendFeedback)
  sendFeedbackRequest.$on('success', responseData => {
    isProcessingSendFeedback.value = false
    $emit('send-feedback-success', responseData)
  })
  sendFeedbackRequest.$on('error', responseErrors => {
    $emit('send-feedback-error', responseErrors)

    if (sendFeedbackRequest.status === 504) {
      errors.value = []
      isProcessingSendFeedback.value = true
      return
    }

    errors.value = normalizeErrors(responseErrors)
  })

  const requestSendFeedback = () => {
    if (!formData.theme || isProcessingSendFeedback.value) return
    errors.value = []
    sendFeedbackRequest.request(formData)
  }

  return {
    isLoadingFeedbackThemes: computed(() => feedbackThemesRequest.isLoading),
    isLoadingSendFeedback: computed(() => sendFeedbackRequest.isLoading),
    isSentSendFeedback: computed(() => sendFeedbackRequest.isFullfilled),
    isProcessingSendFeedback,
    requestFeedbackThemes,
    themes,
    themesOptions,
    formData,
    errors,
    requestSendFeedback
  }
}

export default FeedbackController
